"use client";

import { useCallback, useEffect, useRef } from "react";

import { useFocusTrap } from "../hooks/use-focus-trap";

import styles from "./context-menu.module.scss";

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  "data-testid"?: string;
}

export interface ContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number };
  onClose: () => void;
}

export function ContextMenu({ items, position, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  useFocusTrap(menuRef, { onEscape: onClose });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside as EventListener);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside as EventListener);
    };
  }, [onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const menuItems = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!menuItems || menuItems.length === 0) return;

    const currentIndex = Array.from(menuItems).indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = (currentIndex + 1) % menuItems.length;
        menuItems[next].focus();
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = (currentIndex - 1 + menuItems.length) % menuItems.length;
        menuItems[prev].focus();
        break;
      }
      case "Home": {
        e.preventDefault();
        menuItems[0].focus();
        break;
      }
      case "End": {
        e.preventDefault();
        menuItems[menuItems.length - 1].focus();
        break;
      }
    }
  }, []);

  return (
    <div
      ref={menuRef}
      className={styles.menu}
      style={{ top: position.y, left: position.x }}
      data-testid="context-menu"
      role="menu"
      onKeyDown={handleKeyDown}
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          className={styles.item}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          data-testid={item["data-testid"]}
          role="menuitem"
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
